//useEdgeFade.ts — fades only the edges that actually have more content to scroll to

import { useCallback, useEffect, useRef, useState } from 'react'

export interface EdgeFade {
	readonly ref: (node: HTMLElement | null) => void
	//A permanent right-hand fade reads as "cut off" once you have scrolled to the end, which
	//is what makes a short catalogue look empty. These track the real scroll position.
	readonly atStart: boolean
	readonly atEnd: boolean
	readonly overflows: boolean
	readonly page: (direction: 1 | -1) => void
}

//The strip's 3px side padding plus scroll-snap park the start at scrollLeft ≈ 3, and fractional
//layout widths keep the end off by a pixel, so an exact comparison never reports either edge.
const EDGE_TOLERANCE_PX = 4

export function useEdgeFade(dependency: unknown): EdgeFade {
	const nodeRef = useRef<HTMLElement | null>(null)
	const [state, setState] = useState({ atStart: true, atEnd: true, overflows: false })

	const measure = useCallback(() => {
		const node = nodeRef.current
		if (!node) return
		const { scrollLeft, scrollWidth, clientWidth } = node
		const overflows = scrollWidth - clientWidth > 1
		setState({
			atStart: scrollLeft <= EDGE_TOLERANCE_PX,
			atEnd: !overflows || scrollLeft >= scrollWidth - clientWidth - EDGE_TOLERANCE_PX,
			overflows,
		})
	}, [])

	const ref = useCallback(
		(node: HTMLElement | null) => {
			nodeRef.current = node
			if (node) measure()
		},
		[measure],
	)

	useEffect(() => {
		const node = nodeRef.current
		if (!node) return
		measure()
		node.addEventListener('scroll', measure, { passive: true })
		//Tiles are fluid, so a viewport change alters whether it overflows at all.
		const observer = new ResizeObserver(measure)
		observer.observe(node)
		return () => {
			node.removeEventListener('scroll', measure)
			observer.disconnect()
		}
		//dependency re-measures when the item count changes.
	}, [measure, dependency])

	//Most of a viewport rather than all of it, so the item cut at the edge stays on screen as an anchor.
	const page = useCallback((direction: 1 | -1) => {
		const node = nodeRef.current
		node?.scrollBy({ left: direction * node.clientWidth * 0.8, behavior: 'smooth' })
	}, [])

	return { ref, page, ...state }
}
